import {
  ArcElement,
  Chart as ChartJS,
  type ChartOptions,
  Legend,
  Tooltip,
} from 'chart.js'; // Import necessary chart elements
import { type MouseEvent, useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import styled from 'styled-components';

import { CenterFlex, VFlex } from '@/components/base';
import Typo, { SpanStyle } from '@/components/Typography/Typo';
import { CollapseState } from '@/features/AskSyncly/types/constants';
import type { FeedbackBlockListModalOptions } from '@/features/Feedback/components/FeedbackListModal/types';
import { useSearchDetailContext } from '@/features/GlobalSearch/context/SearchDetailContext';
import useGlobalSearchModalAction from '@/features/GlobalSearch/hooks/useGlobalSearchModalAction';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useColor from '@/hooks/domain/useColor';
import useModalState from '@/hooks/pure/useModalState';
import service from '@/services/index';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { FeedbackSentiment, sentimentMap } from '@/types/feedbackConst';
import { SENTIMENT_COLUMNS } from '@/types/filterKeyConst';
import { GPT_DOMAIN } from '@/types/gptConst';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';
import { getPercentStringFormat } from '@/utils/util';

import SearchDrilldownMenu from '../../SearchDrilldownMenu';

type Props = {
  data: StatisticDataT;
};

ChartJS.register(ArcElement, Tooltip, Legend);

const KeySentimentDistribution = (props: Props) => {
  const { toDetailPage, refreshHistoryList } = useGlobalSearchModalAction();
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const { searchResult } = useSearchDetailContext();
  const { data } = props;

  const color = useColor();

  const { status, open, close, pos, setPos, modalState, setModalState } =
    useModalState<SelectedChartBar>(null);

  const chartData = useMemo(() => {
    return {
      labels: data.buckets.map((bucket) => {
        return sentimentMap[bucket.bucket as FeedbackSentiment].value;
      }),
      datasets: [
        {
          data: data.buckets.map((bucket) => bucket.count),
          backgroundColor: data.buckets.map(
            (bucket) => color.sentiment[bucket.bucket as FeedbackSentiment],
          ),
          meta: data.buckets.map(
            (bucket) => bucket.bucket as FeedbackSentiment,
          ),
          borderColor: color.gray100,
        },
      ],
    };
  }, [color.gray100, color.sentiment, data.buckets]);

  const chartOptions: ChartOptions<'doughnut'> = useMemo(() => {
    return {
      cutout: '75%',
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          display: false,
        },
      },
      onClick: (e: any) => {
        const el = e.chart.tooltip.dataPoints[0];

        if (!el) return;
        const val: SelectedChartBar = {
          dataIndex: el.dataIndex,
          datasetIndex: el.datasetIndex,
        };

        setPos({
          top: e.native.clientY,
          left: e.native.clientX,
        });
        setModalState(val);
        open();
      },
    };
  }, [open, setModalState, setPos]);

  const statistics = useMemo(() => {
    const total = data.buckets.reduce((acc, bucket) => acc + bucket.count, 0);
    const map: Record<
      FeedbackSentiment,
      {
        count: number;
        percentage: number;
      }
    > = {
      [FeedbackSentiment.POSITIVE]: {
        count: 0,
        percentage: 0,
      },
      [FeedbackSentiment.NEUTRAL]: {
        count: 0,
        percentage: 0,
      },
      [FeedbackSentiment.NEGATIVE]: {
        count: 0,
        percentage: 0,
      },
    };

    for (const bucket of data.buckets) {
      map[bucket.bucket as FeedbackSentiment].count = bucket.count;
      map[bucket.bucket as FeedbackSentiment].percentage = Number(
        getPercentStringFormat(total, bucket.count),
      );
    }
    return map;
  }, [data.buckets]);

  const onAddToFilterHandler = async (e: MouseEvent) => {
    e.stopPropagation();
    close();

    if (!modalState) return;

    const sentiment = chartData.datasets[0]?.meta?.[modalState.dataIndex];
    if (!sentiment) return;

    const newFilter = new SFilter(searchResult.filter);
    newFilter.setValueSet(SENTIMENT_COLUMNS.KEY_SENTIMENT, [
      {
        op: STRUCTURED_FILTER_OPERATOR.OR,
        values: [sentiment],
      },
    ]);

    const res = await service.globalSearch.submitSearch({
      ...searchResult,
      title: `${searchResult.title} copy`,
      query: searchResult.query,
      filter: newFilter.get(),
    });

    toDetailPage(res.alias);
    refreshHistoryList();
  };

  const onViewFeedbackListHandler = async (
    e: MouseEvent,
    initialOpen?: boolean,
  ) => {
    e.stopPropagation();
    close();
    if (!modalState) return;
    const sentiment = chartData.datasets[0]?.meta?.[
      modalState?.dataIndex
    ];
    if (!sentiment) return;
    const sentimentValue = sentimentMap[sentiment].value;
    const filterPayload = {
      [SENTIMENT_COLUMNS.KEY_SENTIMENT]:
        structuredFilterUtils.buildSingleValue(sentiment),
    };
    setNewModal<FeedbackBlockListModalOptions>({
      alias: searchResult.alias,
      type: DETAIL_MODAL_TYPE.FEEDBACK_BLOCK_LIST,
      idx: 0,
      keys: ['sentiment', sentimentValue],
      options: {
        queryKeys: ['sentiment', sentimentValue],
        title: 'Feedback list',
        subTitle: `Sentiment: ${sentimentValue}`,
        domain: 'global-search',
        fetcher: async (
          sortStatus: NotNullableSortStatusT,
          pageParam?: null | string,
        ) => {
          const res = await service.globalSearch.fetchSearchFeedbackList(
            searchResult.alias,
            {
              query: {
                cursor: pageParam,
                sortby: sortStatus.sortBy,
                order: sortStatus.orderStatus,
              },
              payload: filterPayload,
            },
          );
          const PAGE_SIZE = 50;
          return {
            count: res.count,
            list: res.feedbacks,
            paginate_token: res.feedbacks[PAGE_SIZE]?.alias,
          };
        },
        exportFetcher: async (reason?: string, password?: string) => {
          await service.globalSearch.exportSearchFeedbackList({
            alias: searchResult.alias,
            filterPayload: filterPayload,
            reason: reason,
            password: password,
          });
        },
        socketInfo: {
          domain: GPT_DOMAIN.GLOBAL_SEARCH,
          search: {
            alias: searchResult.alias,
            filterPayload: filterPayload,
          },
          initialState: initialOpen ? CollapseState.EXPANDED : undefined,
        },
      },
    });
  };

  return (
    <Style>
      <CenterFlex className="chart-wrapper">
        <Doughnut data={chartData} options={chartOptions} />
      </CenterFlex>
      <CenterFlex className="number-display-wrapper" d="column" g={14}>
        <VFlex g={4} a="center">
          <ValueText>
            {statistics[FeedbackSentiment.NEGATIVE].percentage}%
          </ValueText>
          <LabelText>Negative</LabelText>
        </VFlex>
        <VFlex g={4} a="center">
          <ValueText>
            {statistics[FeedbackSentiment.POSITIVE].percentage}%
          </ValueText>
          <LabelText>Positive</LabelText>
        </VFlex>
      </CenterFlex>
      <SearchDrilldownMenu
        pos={{
          top: pos?.top as number,
          left: pos?.left as number,
        }}
        status={status}
        close={close}
        onViewFeedbackListClick={onViewFeedbackListHandler}
        onAddToFilterClick={onAddToFilterHandler}
        onAskSynclyAIClick={(e) => onViewFeedbackListHandler(e, true)}
      />
    </Style>
  );
};

const Style = styled.div`
  position: relative;
  width: 100%;
  height: 170px;
  .chart-wrapper {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
  .number-display-wrapper {
    user-select: none;
    pointer-events: none;
    top: 0;
    left: 0;
    position: absolute;
    width: 100%;
    height: 100%;
  }
`;

const LabelText = styled(Typo)`
  ${SpanStyle({
    type: 'label3',
  })}
  color: ${(props) => props.theme.colors.gray600};
`;

const ValueText = styled(Typo)`
  ${SpanStyle({
    type: 'label1',
    weight: 'sb',
  })}
  color: ${(props) => props.theme.colors.gray800};
`;

export default KeySentimentDistribution;
